// Platform Selection & Reconfiguration component for Settings page
import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Layers, Search, ChevronDown, ChevronUp, CheckCircle, Save, Sparkles,
  Globe, Code, Zap, ArrowRight, X, TrendingUp, Lightbulb,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PlatformIcon, ProviderIcon } from "@/components/logos";
import { PLATFORMS, CATEGORIES, getByCategory, searchPlatforms } from "@/data/platforms";
import { buildAdaptiveConfig, getRecommendations } from "@/lib/adaptiveConfig";
import { staggerItem } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_ICONS = { ai_platform: Sparkles, developer_framework: Code, global_regional: Globe };

export default function PlatformSettings() {
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [originalPlatforms, setOriginalPlatforms] = useState([]);
  const [search, setSearch] = useState("");
  const [expandedTiers, setExpandedTiers] = useState({ ai_platform: true, developer_framework: false, global_regional: false });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dismissedRecs, setDismissedRecs] = useState([]);

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const fetchPlatforms = async () => {
    try {
      const res = await axios.get(`${API}/user/adaptive-config`);
      const platforms = res.data.selected_platforms || [];
      setSelectedPlatforms(platforms);
      setOriginalPlatforms(platforms);
    } catch {
      // Non-blocking
    }
    setLoading(false);
  };

  const togglePlatform = (id) => {
    setSelectedPlatforms(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const toggleTier = (cat) => {
    setExpandedTiers(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const hasChanges = useMemo(() => {
    if (selectedPlatforms.length !== originalPlatforms.length) return true;
    return selectedPlatforms.some(p => !originalPlatforms.includes(p));
  }, [selectedPlatforms, originalPlatforms]);

  const adaptiveConfig = useMemo(() => {
    if (selectedPlatforms.length === 0) return null;
    return buildAdaptiveConfig(selectedPlatforms);
  }, [selectedPlatforms]);

  const recommendations = useMemo(() => {
    if (!adaptiveConfig) return [];
    return getRecommendations(adaptiveConfig.providers, 2)
      .filter(r => !dismissedRecs.includes(r.id));
  }, [adaptiveConfig, dismissedRecs]);

  const handleSave = async () => {
    if (!adaptiveConfig) return;
    setSaving(true);
    try {
      await axios.post(`${API}/user/adaptive-config`, {
        selectedPlatforms,
        adaptiveConfig: {
          providers: adaptiveConfig.providers,
          models: adaptiveConfig.models,
          routingProfileId: adaptiveConfig.routingProfileId,
          savingsEstimate: adaptiveConfig.savingsEstimate,
          configuredAt: adaptiveConfig.configuredAt,
        },
      });
      setOriginalPlatforms(selectedPlatforms);
      toast.success(`Profile updated to "${adaptiveConfig.routingProfile.name}"`);
    } catch {
      toast.error("Failed to update platform configuration");
    }
    setSaving(false);
  };

  const addRecommendedPlatform = (platformId) => {
    if (!selectedPlatforms.includes(platformId)) {
      setSelectedPlatforms(prev => [...prev, platformId]);
    }
  };

  const filteredPlatforms = search.trim() ? searchPlatforms(search) : null;

  const renderPlatformCard = (platform) => {
    const isSelected = selectedPlatforms.includes(platform.id);
    return (
      <motion.button
        key={platform.id}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => togglePlatform(platform.id)}
        data-testid={`settings-platform-${platform.id}`}
        className={`relative flex flex-col items-center justify-center p-2.5 rounded-xl border-2 transition-all duration-150 h-[88px] ${
          isSelected
            ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)] shadow-sm"
            : "border-[var(--qn-border)] hover:border-[var(--qn-border-hover)] bg-[var(--qn-surface)]/50"
        }`}
      >
        {isSelected && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-1 right-1">
            <CheckCircle className="w-3.5 h-3.5 text-[var(--qn-primary)]" />
          </motion.div>
        )}
        <PlatformIcon platformId={platform.id} size="sm" variant="color" />
        <span className="text-xs font-semibold mt-1.5 text-center leading-tight line-clamp-2">{platform.name}</span>
      </motion.button>
    );
  };

  const renderTier = (category) => {
    const cat = CATEGORIES[category];
    const platforms = filteredPlatforms
      ? filteredPlatforms.filter(p => p.category === category)
      : getByCategory(category);
    if (platforms.length === 0) return null;
    const isExpanded = expandedTiers[category];
    const Icon = CATEGORY_ICONS[category];
    const displayPlatforms = isExpanded ? platforms : platforms.slice(0, 4);

    return (
      <div key={category} className="mb-4">
        <button onClick={() => toggleTier(category)} className="flex items-center gap-2 w-full text-left mb-2 group">
          <Icon className="w-3.5 h-3.5 text-[var(--qn-primary)]" />
          <span className="text-sm font-semibold text-[var(--qn-foreground)]">{cat.label}</span>
          <span className="text-xs text-[var(--qn-foreground-subtle)]">({platforms.length})</span>
          <div className="flex-1" />
          {platforms.length > 4 && (
            <span className="text-xs text-[var(--qn-foreground-muted)] flex items-center gap-1">
              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {isExpanded ? "Less" : `+${platforms.length - 4}`}
            </span>
          )}
        </button>
        <div className="grid grid-cols-5 gap-2">
          {displayPlatforms.map(renderPlatformCard)}
        </div>
      </div>
    );
  };

  if (loading) return null;

  return (
    <motion.div variants={staggerItem}>
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-3">
            <Layers className="w-5 h-5 text-[var(--qn-primary)]" />
            Platform Selection
            {adaptiveConfig && (
              <Badge variant="outline" className="ml-auto text-xs font-normal" data-testid="active-profile-badge">
                {adaptiveConfig.routingProfile.name}
              </Badge>
            )}
          </CardTitle>
          <CardDescription className="text-[var(--qn-foreground-muted)]">
            Select platforms to auto-configure your optimization profile
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-subtle)]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search platforms..."
              className="pl-10 bg-[var(--qn-surface)]"
              data-testid="settings-platform-search"
            />
          </div>

          {/* Tiers */}
          <div className="max-h-[300px] overflow-y-auto pr-1" data-testid="settings-platform-tiers">
            {["ai_platform", "developer_framework", "global_regional"].map(renderTier)}
          </div>

          {/* Recommendations */}
          <AnimatePresence>
            {recommendations.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                <div className="flex items-center gap-2 text-sm font-medium text-[var(--qn-foreground)]">
                  <Lightbulb className="w-4 h-4 text-[var(--qn-warning)]" />
                  Recommendations
                </div>
                {recommendations.map(rec => (
                  <motion.div
                    key={rec.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    className="flex items-center gap-3 p-3 rounded-lg border border-[var(--qn-warning)]/20 bg-[var(--qn-warning-muted)]"
                    data-testid={`recommendation-${rec.id}`}
                  >
                    <PlatformIcon platformId={rec.platformId} size="sm" variant="color" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{rec.title}</div>
                      <div className="text-xs text-[var(--qn-foreground-muted)] line-clamp-1">{rec.description}</div>
                    </div>
                    <Badge variant="outline" className="text-[var(--qn-success)] border-[var(--qn-success)]/30 shrink-0">
                      <TrendingUp className="w-3 h-3 mr-1" />+{rec.extraSavings}%
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => addRecommendedPlatform(rec.platformId)}
                      className="shrink-0 text-xs h-7"
                      data-testid={`add-rec-${rec.id}`}
                    >
                      Add
                    </Button>
                    <button
                      onClick={() => setDismissedRecs(prev => [...prev, rec.id])}
                      className="text-[var(--qn-foreground-subtle)] hover:text-[var(--qn-foreground)]"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Current Profile + Save */}
          <div className="flex items-center justify-between pt-2 border-t border-[var(--qn-border)]">
            <span className="text-sm text-[var(--qn-foreground-muted)]">
              {selectedPlatforms.length} platform{selectedPlatforms.length !== 1 ? "s" : ""} selected
              {adaptiveConfig && ` — ${adaptiveConfig.savingsEstimate.savingsRange} savings`}
            </span>
            {hasChanges && (
              <Button
                onClick={handleSave}
                disabled={saving || selectedPlatforms.length === 0}
                className="btn-primary"
                data-testid="save-platforms-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Update Profile"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
